import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@emotion/react';
import { DataGrid } from '@mui/x-data-grid'
import { useAuthContext } from '../context/auth';
import { usePathContext } from '../context/path';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import { Search } from '@mui/icons-material';
import { format } from 'date-fns';
import { Avatar, Box, InputAdornment, Switch, TextField, Typography, useMediaQuery } from '@mui/material';

import { ROLES } from '../config/roles'
import FlexBetween from '../components/FlexBetween';
import Header from '../components/Header';
import PageBanner from '../components/PageBanner';
import { useApplicationContext } from '../context/application';
import { useApplicationTypeContext } from '../context/appType';
import Add from '../components/applications/Add';
import CustomColumnMenu from '../components/DataGridCustomColumnMenu';
import AddAppType from '../components/appTypes/AddAppType';
import DeleteAppType from '../components/appTypes/DeleteAppType';
import Delete from '../components/applications/Delete';
import EditAppType from '../components/appTypes/EditAppType';
import Edit from '../components/applications/Edit';
import Download from '../components/applications/Download';
import { formatFileSize } from '../utils/utils';
import useGridStyles from '../hooks/useGridStyles';

const Application = () => {

  const theme = useTheme();
  const { gridSx, gridBorder } = useGridStyles();
  // is large desktop screen
  const isNonMediumScreen = useMediaQuery("(min-width: 1200px)");

  const { auth } = useAuthContext()
  const { setTitle } = usePathContext()
  const { applications, dispatch } = useApplicationContext()
  const { appTypes, dispatchAppType } = useApplicationTypeContext()
  const [ query, setQuery ] = useState("")
  const [ notFound, setNotFound ] = useState(false)
  const axiosPrivate = useAxiosPrivate()
  const isRoot = auth.roles.includes(ROLES.Root)
  const admin = auth && isRoot
  
  const getIconUrl = (iconPath) => {
    if (!iconPath) return null;

    return `${process.env.REACT_APP_SERVER_URL}${iconPath}`;
  };

  // The App Types card is narrow (2 of 8 columns): fixed widths for No. and Action, Name takes
  // the rest, so the table only scrolls sideways when the card really is too small
  const columnsTypes = [
    {
      field: 'order',
      headerName: 'No.',
      width: 56,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
          if (!params.api || !params.id) return null;
          const index = params.api.getAllRowIds?.().indexOf(params.id);
          return <span>{index + 1}</span>;
      }
    },
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 90,
    },
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      width: 96,
      renderCell: (params) => {
        return (
          <div>
            <EditAppType id={params.row._id} name={ params.row.name } />
            <DeleteAppType id={params.row._id} name={ params.row.name } />
          </div>
        );
      }
    }
  ];

  const columns = [
    { 
      field: 'order', 
      headerName: 'No.', 
      flex: 0.1, 
      sortable: false,
      filterable: false,
      renderCell: (params) => {
          if (!params.api || !params.id) return null;
          const index = params.api.getAllRowIds?.().indexOf(params.id);
          return <span>{index + 1}</span>;
      }
    },
    { 
      field: 'appType', 
      headerName: 'Name', 
      flex: 0.4,
      valueGetter: (params) => {
          const appTypeId = params;
          const appType = appTypes?.find((appType) => appType._id === appTypeId);
          return appType ? appType.name : "*";
      },
    },
    { 
      field: 'version', 
      headerName: 'Version', 
      flex: 0.2,
    },
    { 
      field: 'originalFileName', 
      headerName: 'File Name', 
      flex: 0.55,
      renderCell: (params) => {

        const iconUrl = getIconUrl(params.row.iconPath);

        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 1,
            }}
          >
            {iconUrl && (
              <Avatar
                src={iconUrl}
                alt=""
                variant="rounded"
                sx={{
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                }}
              />
            )}

            <Typography
              variant="body2"
              sx={{
                lineHeight: "36px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {params.value}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'fileSize', 
      headerName: 'File Size', 
      flex: 0.2,
      renderCell: (params) => formatFileSize(params.value),
    },
    { 
      field: 'fileCreatedAt', 
      headerName: 'File Created At', 
      flex: 0.3,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    { 
      field: 'fileUploadedAt', 
      headerName: 'File Uploaded At', 
      flex: 0.3,
      renderCell: (params) => {
        return format(new Date(params.value), 'yyyy-MM-dd HH:mm:ss');
      }
    },
    {
      field: "active",
      headerName: "Active",
      flex: 0.18,
      renderCell: (params) => (
        <Switch
          checked={params.row.active}
          disabled={!isRoot}
          onChange={(event) =>
            handleActiveChange(params.row._id, event.target.checked)
          }
          sx={{
            width: 42,
            height: 24,
            padding: 0,

            "& .MuiSwitch-switchBase": {
              padding: "3px",

              "&.Mui-checked": {
                transform: "translateX(18px)",

                color: theme.palette.getContrastText(
                  theme.palette.secondary.main
                ),

                "& + .MuiSwitch-track": {
                  backgroundColor: theme.palette.secondary.main,
                  opacity: 1,
                },
              },
            },

            "& .MuiSwitch-thumb": {
              width: 18,
              height: 18,
              boxShadow: "0 1px 3px rgba(0,0,0,0.4)",
            },

            "& .MuiSwitch-track": {
              borderRadius: 12,
              backgroundColor: theme.palette.grey[600],
              opacity: 1,
            },
          }}
        />
      ),
    },  
    {
      field: "actions",
      type: "actions",
      headerName: "Action",
      flex: 0.3,
      renderCell: (params) => {
        const application = applications?.find((app) => app._id === params.row._id);
        const appType = appTypes?.find((appType) => appType._id === params.row.appType);

        return (
          <div>
            <Download application={application} />
            {admin && (
              <>
                <Edit application={application} appTypes={appTypes} />
                <Delete id={params.row._id} name={appType ? appType.name : "*"} />
              </>
            )}
          </div>
        );
      }
    }
  ];

  const filteredAppTypes = useMemo(() => {
    const searchValue = query.toLowerCase();
    return (appTypes || []).filter((appType) =>
      appType.name.toLowerCase().includes(searchValue)
    );
  }, [appTypes, query]);

  const filteredApplications = useMemo(() => {
    if (!applications) return [];

    const searchValue = query.toLowerCase();

    return applications.filter((application) => {
      const appType = appTypes?.find((type) => type._id === application.appType);

      return [
        application.version,
        application.originalFileName,
        appType?.name,
        application.fileSize,
      ].some((value) => String(value || '').toLowerCase().includes(searchValue));
    });
  }, [applications, appTypes, query]);

  const handleActiveChange = async (id, active) => {
    try {
      const response = await axiosPrivate.patch(
        `/api/applications/${id}/active`, 
        { active, }
      );

      dispatch({ type: "UPDATE_APPLICATION", payload: response.data, });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
      setTitle("Application Management")
      
      const abortController = new AbortController()

      const getAllApplications = async () => {
        try {
          const response = await axiosPrivate.get('/api/applications', {
            signal: abortController.signal
          })
          dispatch({type: 'SET_APPLICATION', payload: response.data})
          setNotFound(false)
        } catch (err) {
          // console.log(err)
          setNotFound(true)
        }
      }

      const getAllAppTypes = async () => {
        try {
          const response = await axiosPrivate.get('/api/appTypes', {
            signal: abortController.signal
          })
          dispatchAppType({type: 'SET_APPLICATION_TYPE', payload: response.data})
          setNotFound(false)
        } catch (err) {
          // console.log(err)
          setNotFound(true)
        }
      }
      
      if(auth){
        getAllAppTypes()
        getAllApplications()
      }

      return () => {
        abortController.abort()
      }
  }, [setTitle, auth, dispatch, axiosPrivate, dispatchAppType])

  return (
    <>
      {/* Fill the page area under the navbar exactly: header on top, the two cards take the
          rest, so the page itself never needs a scrollbar on wide screens */}
      <Box
        sx={{
          height: isNonMediumScreen ? "100%" : "auto",
          boxSizing: "border-box",
          p: "1.5rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <FlexBetween 
          sx={{
            flexDirection: {
              xs: 'column',
              sm: 'row',
            },
            alignItems: {
              xs: 'flex-start',
              sm: 'center',
            },
            gap: '1rem',
          }}
        >
          {/* Header */}
          <Header
            title="Applications"
            subtitle={admin ? "List Applications and Add, Edit or Delete Applications" : "List Applications and Download Applications"}
          />
          <PageBanner />

          <FlexBetween>       
            {/* Search */}
            <TextField
                label="Search by Application Name"
                placeholder="Type to search..."
                variant="standard"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                sx={{
                    mb: "0.5rem",
                    mr: "1rem",
                    width: "15rem",
                }}
                InputProps={{
                    endAdornment: (
                    <InputAdornment position="end">
                        <Search sx={{ color: theme.palette.secondary[200] }} />
                    </InputAdornment>
                    ),
                }}
            />                                         
          </FlexBetween>
        </FlexBetween>      

        <Box
          mt="1rem"
          display="grid"
          gridTemplateColumns={admin ? "repeat(8, 1fr)" : "repeat(6, 1fr)"}
          gap="20px"
          sx={{
            // Wide screens: one row that fills the space left under the header.
            // Narrow screens: cards stack, each with its own height, and the page scrolls.
            flex: isNonMediumScreen ? 1 : "none",
            minHeight: isNonMediumScreen ? 320 : 0,
            gridTemplateRows: isNonMediumScreen ? "minmax(0, 1fr)" : "none",
            gridAutoRows: isNonMediumScreen ? undefined : "70vh",

            "& > div": {
              minHeight: 0,
              gridColumn: isNonMediumScreen ? undefined : "span 12",
            },
          }}
        >
          {admin && (
            <Box
              gridColumn="span 2"
              gridRow="span 1"
              display="flex"
              flexDirection="column"
              justifyContent="space-between"
              p="1.25rem 1rem"
              flex="1 1 100%"
              backgroundColor={theme.palette.background.alt}
              border={gridBorder}
              borderRadius="0.55rem"
              sx={{
                padding: "0px",
                minHeight: 0,
              }}
              overflow="hidden"
            >  
              <FlexBetween>
                <Typography variant="h4" sx={{ color: theme.palette.secondary[200], width: '70%', margin: 'normal', padding: "10px 10px" }}>
                  App Types ({filteredAppTypes.length})
                </Typography>

                <AddAppType appTypes={appTypes} />                                        
              </FlexBetween>      
              {/* Fill the rest of the card (like App Details). A fixed 100vh - 3rem height was
                  taller than the card, so the grid's bottom edge, footer and horizontal scrollbar
                  were cut off by the card's overflow: hidden. */}
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                }}
              >
                {/* Grid Table */}
                <DataGrid
                  loading={!appTypes}
                  getRowId={(row) => row._id}
                  rows={filteredAppTypes}
                  columns={columnsTypes}
                  disableRowSelectionOnClick
                  components={{
                      ColumnMenu: CustomColumnMenu,
                  }}
                  sx={{
                    ...gridSx,
                    // Narrow card: drop the "Rows per page:" label and tighten the footer so the
                    // pagination fits instead of scrolling sideways
                    '& .MuiTablePagination-selectLabel': { display: 'none' },
                    '& .MuiTablePagination-input': { ml: 0, mr: 1 },
                    '& .MuiTablePagination-actions': { ml: 0.5 },
                    '& .MuiTablePagination-toolbar': { ...gridSx['& .MuiTablePagination-toolbar'], pl: 1 },
                  }}
                />
              </Box>
            </Box>
          )}
          <Box
            gridColumn="span 6"
            gridRow="span 1"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            p="1.25rem 1rem"
            flex="1 1 100%"
            backgroundColor={theme.palette.background.alt}
            border={gridBorder}
            borderRadius="0.55rem"
            sx={{
              padding: "0px",
              minHeight: 0,
            }}
            overflow="hidden"
          >  
            <FlexBetween>
              <Typography variant="h4" sx={{ color: theme.palette.secondary[200], width: '70%', margin: 'normal', padding: "10px 10px" }}>
                App Details ({filteredApplications.length})
              </Typography>
              {admin && (
                <Add applications={applications} appTypes={appTypes} />  
              )}                                  
            </FlexBetween>      
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: "100%",
              }}
            >              
              {/* Grid Table */}
              <DataGrid
                loading={!applications}
                getRowId={(row) => row._id}
                rows={filteredApplications}
                columns={columns}
                disableRowSelectionOnClick
                components={{
                    ColumnMenu: CustomColumnMenu,
                }}
                sx={gridSx}
              />
            </Box>   
          </Box>
        </Box>  
      </Box>
    </> 
  );
}

export default Application;